import { Accordion, AccordionItem } from '@heroui/react';
import NextLink from 'next/link';

import { BlogPageLayout } from '../../components/blog/BlogPageLayout';
import { Section } from '../../components/layout/Storyblok/Section/Section';
import wysiwygStyles from '../../components/layout/Storyblok/Wysiwyg/Wysiwyg.module.scss';
import { CTASection } from '../../components/shared/CTASection';
import { FAQStructuredData } from '../../components/shared/FAQStructuredData';
import { KeyTakeaways } from '../../components/shared/KeyTakeaways';
import { Icon } from '../../components/various/Icon';
import { useSession } from '../../hooks/useSession';
import { SectionStoryblok } from '../../typings/storyblok';
import { getHeadingId } from '../../utils/storyblok';

const DOCUMENT_TITLE = 'AI Image Generator and Text-to-Video Tools for Creative Teams (2026)';
const META_DESCRIPTION =
  'Create campaign stills and short video drafts with AI image generators and text-to-video tools, then review cuts with timestamped feedback before you ship.';
const PUBLISH_DATE = '2026-08-26 00:00';
const READ_TIME = '11';
const CANONICAL_PATH = '/blog/ai-image-generator-and-text-to-video-for-creative-teams';
const COVER_SRC = '/images/blog/ai-image-generator-and-text-to-video-for-creative-teams-cover.png';

const HERO_BLOK: SectionStoryblok = {
  _uid: 'static-ai-image-generator-text-to-video-hero',
  component: 'section',
  title: DOCUMENT_TITLE,
  titleTag: 'h1',
  text: 'AI drafts remove the blank page. Review tools remove the email chaos. Here is how creative teams combine both.',
  orientation: '',
  displaySocials: false,
  imageObjectFit: 'cover',
  image: {
    _uid: 'static-ai-image-generator-text-to-video-cover',
    id: null,
    alt: 'AI image generator and text-to-video workflow for creative teams',
    name: '',
    focus: '',
    source: '',
    title: DOCUMENT_TITLE,
    filename: COVER_SRC,
    copyright: '',
    fieldtype: 'asset',
  },
};

const TOC_SECTIONS = [
  'Why create and review are separate jobs',
  'AI image generator for campaign stills',
  'Text to video for draft clips',
  'From AI draft to client approval',
  'Which workflow fits your team',
  'Frequently asked questions',
] as const;

const FAQS = [
  {
    question: 'What is an AI image generator used for on a creative team?',
    answer:
      'Teams use an AI image generator to turn prompts into stills for ads, landing pages, thumbnails, mockups, and moodboards—especially when stock libraries are slow or off-brand. It is a draft and exploration step, not a replacement for brand art direction or final retouch.',
  },
  {
    question: 'How is text to video different from filming a first cut?',
    answer:
      'Text to video generates a short clip from a written prompt so you can test hooks, scene ideas, and aspect ratios before a shoot or full edit. It is strongest for concepts, explainers, and social drafts—not for replacing a finished edit with real talent footage when that is the brief.',
  },
  {
    question: 'Should AI outputs go straight to clients?',
    answer:
      'Usually no. AI drafts still need a review pass: pick the best variation, check brand fit, leave timestamped notes on motion, and confirm who approved which version. That is where a video review workspace matters more than another generation click.',
  },
  {
    question: 'Can SaaS and tech marketing teams use the same stack?',
    answer:
      'Yes. Product marketers often generate UI-adjacent stills and short demo teasers with AI, then collect stakeholder notes on the same review link. Teams that also buy growth help typically look for technology digital marketing services that understand product launches, not only brand campaigns.',
  },
  {
    question: 'Is there a free way to try AI image and text-to-video tools?',
    answer:
      'Magic Hour offers free daily generations for both its AI image generator and text to video tools in the browser, with paid plans for higher volume, longer clips, and commercial use. Always confirm current free-tier limits and watermark rules on their product pages.',
  },
] as const;

export default function AiImageGeneratorAndTextToVideoForCreativeTeamsPage() {
  useSession();

  const tocLinks = TOC_SECTIONS.map((label) => ({
    label,
    url: `#${getHeadingId(label)}`,
  }));

  return (
    <>
      <FAQStructuredData faqs={[...FAQS]} />
      <BlogPageLayout
        documentTitle={DOCUMENT_TITLE}
        metaDescription={META_DESCRIPTION}
        canonicalPath={CANONICAL_PATH}
        ogImageUrl={COVER_SRC}
        ogImageAlt="AI image generator and text-to-video workflow for creative teams"
        publishDate={PUBLISH_DATE}
        readTimeMinutes={READ_TIME}
        breadcrumbLabel="AI image and text-to-video tools"
        tocLinks={tocLinks}
      >
        <Section blok={HERO_BLOK} />

        <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-transparent p-6 sm:p-8">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex-shrink-0 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 p-2">
              <Icon icon="checkCircle" size={18} className="text-primary" />
            </div>
            <p className="font-sans text-base font-bold">Article summary</p>
          </div>
          <p className="pl-1 text-sm leading-relaxed text-foreground-600 sm:text-base">{META_DESCRIPTION}</p>
        </div>

        <div className={wysiwygStyles.wysiwyg}>
          <p>
            Creative teams ship faster when generation and approval are clear stages. An{' '}
            <a href="https://magichour.ai/products/ai-image-generator" target="_blank" rel="noopener noreferrer">
              AI image generator
            </a>{' '}
            and a{' '}
            <a href="https://magichour.ai/products/text-to-video" target="_blank" rel="noopener noreferrer">
              text to video
            </a>{' '}
            tool get you from brief to draft. A{' '}
            <NextLink href="/free-tools/video-reviewer">free online video review tool</NextLink> gets you from draft to
            signed-off cut without dumping files into Slack threads.
          </p>
          <p>
            This guide focuses on that create-then-review loop for agencies, in-house studios, and SaaS product
            marketers—including teams that also rely on{' '}
            <a
              href="https://discovermybusiness.co/industries-we-help/technology-digital-marketing-services/"
              target="_blank"
              rel="noopener noreferrer"
            >
              technology digital marketing services
            </a>{' '}
            for launch and demand programs.
          </p>

          <KeyTakeaways
            items={[
              'Use AI stills and text-to-video for exploration; use review software for decisions.',
              'Magic Hour covers browser-based image generation and prompt-to-video drafts in one platform.',
              'Timestamped feedback on the chosen draft prevents “which file did we approve?” chaos.',
            ]}
          />

          <h2 id={getHeadingId(TOC_SECTIONS[0])}>{TOC_SECTIONS[0]}</h2>
          <p>
            Generation tools optimize for volume and speed. Review tools optimize for clarity: who commented, on which
            frame, against which version. Mixing those jobs in one chat channel is how campaigns slip dates.
          </p>
          <p>
            Treat AI output as a deliverable candidate. Name the file, share one review link, collect notes, then
            regenerate or edit only against that feedback—not against five parallel Slack opinions.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[1])}>{TOC_SECTIONS[1]}</h2>
          <p>
            <a href="https://magichour.ai/products/ai-image-generator" target="_blank" rel="noopener noreferrer">
              Magic Hour&apos;s AI image generator
            </a>{' '}
            turns a text prompt into stills in the browser—no install required to start. It supports multiple frontier
            models, batch variations, text-to-image and image-to-image inputs, style presets, and an upscaler path when
            you need sharper marketing assets.
          </p>
          <p>
            <strong>Best for:</strong> ad concepts, blog illustrations, product mockups, thumbnails, and moodboards when
            you need many options before art direction locks.
          </p>
          <p>
            <strong>Where it fits the pipeline:</strong> generate 8–16 variations, pick two contenders, then attach them
            to the same campaign folder as the video drafts so stakeholders see the visual system together.
          </p>
          <p>
            <strong>Practical tip:</strong> write prompts with subject, setting, lighting, and brand constraints. Save
            the winning prompt language so later regenerations stay on-brand across the series.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[2])}>{TOC_SECTIONS[2]}</h2>
          <p>
            <a href="https://magichour.ai/products/text-to-video" target="_blank" rel="noopener noreferrer">
              Magic Hour&apos;s text to video
            </a>{' '}
            tool generates a downloadable clip from a scene description. You pick an aspect ratio, generate, preview, and
            iterate—useful before a shoot budget is spent or before an editor builds a full timeline.
          </p>
          <p>
            Model choice matters: creative teams often rotate between motion-heavy models and realism-focused ones
            depending on whether the draft is a social hook, an explainer beat, or a launch teaser. Prompt for camera
            move, action, and mood the same way you would brief a motion designer.
          </p>
          <p>
            <strong>Best for:</strong> concept tests, short social drafts, storyboard-style scenes, and product-launch
            teasers that need motion before footage exists.
          </p>
          <p>
            <strong>Where it falls short:</strong> free tiers may limit resolution, daily generations, or watermark-free
            export. Confirm commercial-use rules on paid plans before shipping client work.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[3])}>{TOC_SECTIONS[3]}</h2>
          <p>
            Once an AI clip is “good enough to discuss,” move it into review—not back into another generation binge.
            Upload the MP4 to a{' '}
            <NextLink href="/free-tools/video-reviewer">video reviewer</NextLink>, invite guests, and collect
            frame-accurate comments on pacing, on-screen text, and brand fit.
          </p>
          <ol>
            <li>Export the strongest AI draft (and one backup variation).</li>
            <li>
              Open a shared review link with{' '}
              <NextLink href="/free-tools/video-feedback-tool">video feedback</NextLink> enabled for stakeholders.
            </li>
            <li>Resolve notes into a short punch list: regenerate, edit in NLE, or approve as-is.</li>
            <li>Archive the approved version next to the stills so the campaign kit stays coherent.</li>
          </ol>
          <p>
            For annotation-heavy passes, pair the player with a{' '}
            <NextLink href="/free-tools/video-annotator">video annotator</NextLink> workflow so drawings and timestamps
            stay attached to the cut.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[4])}>{TOC_SECTIONS[4]}</h2>
          <ul>
            <li>
              Need on-brand stills fast →{' '}
              <a href="https://magichour.ai/products/ai-image-generator" target="_blank" rel="noopener noreferrer">
                AI image generator
              </a>
            </li>
            <li>
              Need a motion draft from a prompt →{' '}
              <a href="https://magichour.ai/products/text-to-video" target="_blank" rel="noopener noreferrer">
                text to video
              </a>
            </li>
            <li>
              Need stakeholder approval on the chosen cut →{' '}
              <NextLink href="/free-tools/video-reviewer">Kreatli video reviewer</NextLink>
            </li>
            <li>
              Comparing review platforms after the draft stage →{' '}
              <NextLink href="/comparisons/frameio-alternatives-2026">Frame.io alternatives (2026)</NextLink>
            </li>
          </ul>
          <p>
            If the next problem is production structure around the review—not just the player—see{' '}
            <NextLink href="/comparisons/filestage-alternatives-2026">Filestage alternatives</NextLink> and{' '}
            <NextLink href="/guides/how-to-review-a-video">how to review a video</NextLink>.
          </p>

          <hr />

          <h2 id={getHeadingId(TOC_SECTIONS[5])}>{TOC_SECTIONS[5]}</h2>
        </div>

        <Accordion variant="splitted" aria-label="AI image and text-to-video FAQs" className="gap-2">
          {FAQS.map((faq) => (
            <AccordionItem key={faq.question} title={faq.question} classNames={{ title: 'text-left font-semibold' }}>
              {faq.answer}
            </AccordionItem>
          ))}
        </Accordion>

        <CTASection
          title="Go ahead and start using Kreatli for free!"
          description="Upload your AI draft, collect timestamped feedback, and finish the approval cycle in one workspace."
          primaryButtonText="Start for Free"
          primaryButtonHref="/sign-up"
        />
      </BlogPageLayout>
    </>
  );
}
